import React, { useState } from 'react';
import { useGetOverviews } from 'api/hooks/useGetOverviews';
import { usePostProposalsStatus } from 'api/hooks/usePostProposalsStatus';
import { ActionItems, SummaryCard } from 'features/Layout/PageActions';
import { SuspenseWrapper } from 'features/SuspenseWrapper/SuspenseWrapper';
import type { IProposalStatusPayload } from 'api/resources/proposals';
import { Modal, Button, Icon } from 'ui';
import { useStackAnimation } from 'app/hooks/useStackAnimation';
import { motion } from 'framer-motion';
import { ProposalCard } from './partials/ProposalCard';
import { ActionModalContent } from './partials/ActionModalContent';
import { CustomModalTitle } from './partials/CustomModalTitle';
import { useForm, FormProvider } from 'react-hook-form';
import { EStatus } from 'ui/types/core';
import type { IProposals } from 'api/resources/overviews';
import {
	LayoutContent,
	LayoutHeader,
	LayoutWrapper,
} from 'app/features/Layout';

export const Proposals = () => {
	const { data, isLoading } = useGetOverviews();
	const { mutate: postProposalStatus } = usePostProposalsStatus();
	const [selectedProposals, setSelectedProposals] = useState<IProposals[]>([]);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const methods = useForm<IProposalStatusPayload>({
		mode: 'onChange',
		defaultValues: {
			signature: '',
			termsAccepted: false,
		},
	});

	const proposals = data?.data?.proposals || [];

	const totalProposed = proposals.reduce(
		(sum, proposal) => sum + proposal.total,
		0,
	);
	const depositRequested = proposals.reduce(
		(sum, proposal) => sum + proposal.deposit,
		0,
	);
	const previousPayments = 1000;
	const balanceDue = totalProposed - previousPayments;

	const items = [
		{ label: 'Total Proposed', amount: totalProposed },
		{ label: 'Deposit Requested', amount: depositRequested },
		{ label: 'Previous Payments', amount: previousPayments },
		{ label: 'Balance Due', amount: balanceDue },
	];

	const handleModalAction = (data: IProposalStatusPayload) => {
		if (data.signature && data.termsAccepted) {
			const payloads: IProposalStatusPayload[] = selectedProposals.map(
				(proposal) => ({
					id: proposal.id,
					status: EStatus.Signed,
					signature: data.signature,
					termsAccepted: data.termsAccepted,
				}),
			);
			postProposalStatus(payloads);
			setSelectedProposals([]);
			setIsModalOpen(false);
			methods.reset();
		}
	};

	const handleDecline = (proposal: IProposals) => {
		const payload: IProposalStatusPayload = {
			id: proposal.id,
			status: EStatus.Declined,
		};
		postProposalStatus([payload]);
	};

	const handleOpenPdf = (url: string) => {
		window.open(url, '_blank');
	};

	const toggleSelection = (proposal: IProposals) => {
		setSelectedProposals((prev) =>
			prev.some((p) => p.id === proposal.id)
				? prev.filter((p) => p.id !== proposal.id)
				: [...prev, proposal],
		);
	};

	const handleOpenModal = (proposal: IProposals) => {
		setSelectedProposals([proposal]);
		setIsModalOpen(true);
	};

	const motionProps = useStackAnimation(0.3, !isLoading);

	const totalItems = selectedProposals.reduce(
		(sum, proposal) => sum + proposal.quantity,
		0,
	);

	return (
		<LayoutWrapper>
			<LayoutHeader
				title="Proposals"
				icon="queue-list"
				actions={
					<Button
						key="btn-approve-sign-all"
						variant="primary"
						onClick={() => setIsModalOpen(true)}
						disabled={selectedProposals.length === 0}
						size="sm"
					>
						<Icon name="check-circle" className="mr-2 fill-white" />
						Approve & Sign All
					</Button>
				}
			/>
			<LayoutContent className="px-4 lg:px-28 pt-6 lg:pt-25">
				<SuspenseWrapper>
					<SummaryCard items={items} isDataReady={!isLoading} />
					<ActionItems title="Proposals to Review">
						{proposals.map((proposal, index) => (
							<motion.div
								key={index}
								initial={motionProps.initial}
								animate={motionProps.animate}
								custom={index}
								className="w-full"
							>
								<ProposalCard
									key={proposal.id}
									proposal={proposal}
									isSelected={selectedProposals.some(
										(p) => p.id === proposal.id,
									)}
									onSelect={toggleSelection}
									onOpenPdf={handleOpenPdf}
									onOpenModal={handleOpenModal}
									onDecline={handleDecline}
								/>
							</motion.div>
						))}
					</ActionItems>
				</SuspenseWrapper>
				<Modal
					isOpen={isModalOpen}
					onClose={() => {
						setIsModalOpen(false);
						setSelectedProposals([]);
						methods.reset();
					}}
					showCloseButton={false}
					className="w-[35%]"
				>
					<CustomModalTitle
						proposals={selectedProposals.map((p) => ({
							id: p.id,
							title: p.title,
							date: p.date,
						}))}
						totalItems={totalItems}
					/>
					<FormProvider
						resetField={methods.resetField}
						getFieldState={methods.getFieldState}
						handleSubmit={methods.handleSubmit}
						watch={methods.watch}
						setError={methods.setError}
						getValues={methods.getValues}
						reset={methods.reset}
						setValue={methods.setValue}
						clearErrors={methods.clearErrors}
						control={methods.control}
						register={methods.register}
						unregister={methods.unregister}
						setFocus={methods.setFocus}
						trigger={methods.trigger}
						formState={methods.formState}
					>
						<form onSubmit={methods.handleSubmit(handleModalAction)}>
							<div className="p-3">
								<ActionModalContent />
								<div className="w-full flex gap-1 justify-between mt-6">
									<Button
										variant="secondary"
										className="w-1/2"
										onClick={() => {
											setIsModalOpen(false);
											setSelectedProposals([]);
											methods.reset();
										}}
									>
										Cancel
									</Button>
									<Button
										variant="primary"
										className="w-1/2"
										type="submit"
										disabled={!methods.formState.isValid}
									>
										<Icon name="check-circle" className="mr-2 fill-white" />
										Accept
									</Button>
								</div>
							</div>
						</form>
					</FormProvider>
				</Modal>
			</LayoutContent>
		</LayoutWrapper>
	);
};

Proposals.displayName = 'Proposals';
